import React from "react";
import { PropTypes } from "prop-types";
import { Card, Button, Row, Col } from "react-bootstrap";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { BiPlusMedical, BiLinkExternal, BiTrash } from "react-icons/bi";
import { FaEdit } from "react-icons/fa";

import AppBreadcrumb from "views/Admin/Layout/AppBreadCrumb";
import PiDataTable from "views/DataTable/PiDataTable";

import {
  getSchoolsList,
  resetComponentStore,
  deleteSchool,
} from "actions/schoolActions";

import SchoolFilters from "./SchoolFilters";

const SchoolsList = ({
  schoolsList: { data, count },
  getSchoolsList,
  loadingSchoolsList,
  resetComponentStore,
  sortingParams,
  loggedInUser,
  deleteSchool,
}) => {
  const initialSortingParams = {
    limit: 20,
    page: 1,
    orderBy: "createdAt",
    ascending: "desc",
    query: "",
    filters: [],
  };

  const { page, limit } = sortingParams;

  const [params, setParams] = React.useState(initialSortingParams);

  const [onlyOnce, setOnce] = React.useState(true);
  const actions = (
    <div className="page-actions">
      <Link to="/admin/schools/create" title="create">
        <Button color="primary" size="sm">
          <BiPlusMedical /> Create New School
        </Button>
      </Link>
    </div>
  );

  const columns = [
    {
      name: "Name",
      selector: (row) => (
        <React.Fragment>
          {row.name}
          {row.status === 3 ? <span className="sticker-new">New</span> : null}
        </React.Fragment>
      ),
      sortable: false,
      width: "30%",
      wrap: true,
    },
    {
      name: "Address",
      selector: (row) => (
        <React.Fragment>
          {row.address_line1 ? (
            <React.Fragment>
              {row.address_line1} <br></br>
            </React.Fragment>
          ) : (
            ""
          )}
          {row.address_line2 ? (
            <React.Fragment>
              {row.address_line2} <br></br>
            </React.Fragment>
          ) : (
            ""
          )}
          <React.Fragment>
            {row.city
              ? row.city && row.state
                ? `${row.city}, `
                : row.city
              : ""}
            {row.state ? row.state : ""} {row.zipcode ? row.zipcode : ""}
          </React.Fragment>
        </React.Fragment>
      ),
      sortable: false,
      width: "15%",
    },
    {
      name: "URL",
      selector: (row) => (
        <React.Fragment>
          {row.url ? (
            <a href={`${row.url}`} target="_blank">
              {row.url} <BiLinkExternal />
            </a>
          ) : null}
        </React.Fragment>
      ),
      sortable: false,
      width: "25%",
      wrap: true,
    },
    {
      name: "Phone",
      selector: (row) => row.phone,
      sortable: false,
      width: "10%",
    },
    {
      name: "Actions",
      width: "calc(20% - 48px)",
      button: true,
      cell: (row) => (
        <div className="App table-list-buttons">
          <Link to={`/admin/schools/${row._id}`} title="View Edit School">
            <Button variant="primary" size="sm">
              <FaEdit />
            </Button>
          </Link>

          <Button
            className="ml-1"
            size="sm"
            title="Delete"
            type="button"
            variant="danger"
            onClick={(e) => {
              if (
                window.confirm(`Are you sure you want to delete: ${row.name}?`)
              ) {
                deleteSchool(row._id);
              }
            }}
          >
            <BiTrash />
          </Button>
        </div>
      ),
    },
  ];

  React.useMemo(() => {
    if (onlyOnce) {
      resetComponentStore();
      setOnce(false);
    }

    if (!loggedInUser) return;

    getSchoolsList(params);
  }, [getSchoolsList, params, resetComponentStore, loggedInUser]);

  const handleTableChange = (type, searchText) => {
    let params = {
      limit: limit,
      page: type === "search" ? 1 : page ? page : 1,
    };

    let filters = [];
    if (type === "search") {
      if (searchText.length > 0) {
        filters = sortingParams.filters.includes(type)
          ? sortingParams.filters
          : [...sortingParams.filters, type];
        params = {
          ...params,
          query: {
            ...sortingParams.query,
            [type]: {
              name: {
                value: searchText,
                type: "String",
              },
              city: {
                value: searchText,
                type: "String",
              },
              state: {
                value: searchText,
                type: "String",
              },
              zipcode: {
                value: searchText,
                type: "String",
              },
              phone: {
                value: searchText,
                type: "String",
              },
            },
          },
          filters,
        };
      } else {
        filters = sortingParams.filters.filter((item) => item !== type);
        const temp = {};
        params = {
          ...sortingParams,
          filters,
        };
        for (var i in params.query) {
          if (i === type) continue;
          temp[i] = params.query[i];
        }
        params.query = temp;
      }
    }

    setParams(params);
  };

  const onFilterChange = (newParams) => {
    setParams((params) => ({ ...params, ...newParams }));
  };

  return (
    <React.Fragment>
      <AppBreadcrumb pageTitle="Schools" crumbs={[{ name: "Schools" }]} />

      <Card>
        <Card.Body>
          {actions}

          <div className="table-filter-section">
            <Row>
              <Col md="6">
                <SchoolFilters
                  type="text"
                  onSearch={handleTableChange}
                  filterType="String"
                  filterParams={params}
                  onFilterChange={onFilterChange}
                />
              </Col>
            </Row>
          </div>

          <PiDataTable
            columns={columns}
            data={data}
            count={count}
            params={params}
            setParams={setParams}
            pagination
            responsive
            striped={true}
            selectableRows
            progressPending={loadingSchoolsList}
            highlightOnHover
            persistTableHead={true}
            paginationServer
          />
        </Card.Body>
      </Card>
    </React.Fragment>
  );
};

SchoolsList.propTypes = {
  getSchoolsList: PropTypes.func.isRequired,
  deleteSchool: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  schoolsList: state.school.schoolsList,
  loadingSchoolsList: state.school.loadingSchoolsList,
  sortingParams: state.school.sortingParams,
  loggedInUser: state.auth.user,
});

export default connect(mapStateToProps, {
  getSchoolsList,
  resetComponentStore,
  deleteSchool,
})(SchoolsList);
